import { useEffect, useRef } from 'react'
import { Property } from '../types/property'
import PropertyCard from './PropertyCard'

interface ListingsPanelProps {
  properties: Property[]
  loading: boolean
  error: string | null
  onLoadMore: () => void
  hasMore: boolean
  onPropertySelect: (property: Property) => void
  selectedProperty: Property | null
}

export default function ListingsPanel({
  properties,
  loading,
  error,
  onLoadMore,
  hasMore,
  onPropertySelect,
  selectedProperty,
}: ListingsPanelProps) {
  const observerTarget = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          onLoadMore()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => observer.disconnect()
  }, [hasMore, loading, onLoadMore])

  if (error) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Properties</h3>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    )
  }

  if (!loading && properties.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <div className="text-center">
          <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Properties Found</h3>
          <p className="text-gray-600">Try adjusting your search filters</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4">
      {properties.map((property) => (
        <PropertyCard
          key={property.listingKey}
          property={property}
          onSelect={onPropertySelect}
          isSelected={selectedProperty?.listingKey === property.listingKey}
        />
      ))}

      {loading && (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
        </div>
      )}

      <div ref={observerTarget} className="h-4" />
    </div>
  )
}
